import _ from 'lodash';

import { PaginationRequest, PaginationResponse, PaginationFilter, SortOrder } from './PaginationParameters';
import { classToClassFromExist } from 'class-transformer';

type LoaderFunc<T> = (pagination: PaginationRequest)=>Promise<{ data: T[], pagination?: PaginationResponse }>;

export class Loadable<T extends { id: string }>
{
    public data: T[];
    public originalData!: T[];
    public isLoading: boolean = false;
    public isError: boolean = false;
    public pagination: PaginationResponse = new PaginationResponse();

    private _searchValue: string = '';

    private _statusFilter: string = '';

    private _platformFilter: string = '';

    public sortField: string = 'id';
    private sortOrder: SortOrder = 'desc';

    public find(id: string) 
    {
        return this.data.find((issue: any) => {
            return issue.forgeId == id;
        });
    }

    public sortLocal(field: string, order: SortOrder)
    {
        this.data.sort((a: any, b: any) => {
            return a[field] > b[field] ? (order == 'asc' ? 1 : -1) : (order == 'asc' ? -1 : 1);
        });
    }

    public setSorting(sortField: string, sortOrder: SortOrder)
    {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.loadPage(this.currentPage);
    }

    private debouncedLoad = _.debounce(() => {
        this.loadPage(this.currentPage);
    }, 250);

    public get isSearchEnabled()
    {
        return !!this.searchField;
    }

    public get statusFilter()
    {
        return this._statusFilter;
    }

    public set statusFilter(value: string)
    {
        if (value != this._statusFilter)
        {
            this._statusFilter = value;

            if (this.isSearchEnabled)
            {
                this.debouncedLoad();
            }
        }
    }

    public get platformFilter() {
        return this._platformFilter;
    }

    public set platformFilter(value: string) {
        if (value != this._platformFilter) {
            this._platformFilter = value;

            if (this.isSearchEnabled) {
                this.debouncedLoad();
            }
        }
    }

    public get searchValue()
    {
        return this._searchValue;
    }

    public set searchValue(value: string)
    {
        if (value != this._searchValue)
        {
            this._searchValue = value;

            if (this.isSearchEnabled)
            {
                this.debouncedLoad();
            }
        }
    }

    public _currentPage = 0;

    private loader: LoaderFunc<T> = async (pagination: PaginationRequest) => ({ data: this.defaultData });

    constructor(private ctor: new () => T, public defaultData: T[], public searchField?: string)
    {
        this.data = defaultData;
        this.updateOriginalData();
    }

    public getItemWithID(id: string): T
    {
        for (let item of this.data)
        {
            if (item.id == id)
            {
                return item;
            }
        }

        let item = new this.ctor();
        item.id = id;
        this.data.push(item);
        return item;
    }

    private onDataLoaded(data: T[])
    {
        let oldData = this.data;
        let newData:T[] = [];

        for (let item of data)
        {
            let matchingItem = oldData.find(od => od.id == item.id);

            if (matchingItem)
            {
                classToClassFromExist(item, matchingItem);
                newData.push(matchingItem);
            }
            else
            {
                newData.push(item);
            }
        }

        this.data = newData;
    }

    public async load(loader: LoaderFunc<T>)
    {
        this.loader = loader;
        await this.loadPage(0);
    }

    public clear() {
        this.data = [];
        this.originalData = [];
    }

    public get currentPage()
    {
        return this._currentPage;
    }

    public set currentPage(page: number)
    {
        this.loadPage(page);
    }

    public async changePage(page: number): Promise<any>
    {
        return await this.loadPage(page);
    }

    private updateOriginalData()
    {
        this.originalData = _.cloneDeep(this.data);
    }

    public revertToOriginalData()
    {
        this.data = _.cloneDeep(this.originalData);
    }

    private async loadPage(page: number)
    {
        this._currentPage = page;
        this.isLoading = true;
        this.isError = false;
        if(this.defaultData.length) this.data = this.defaultData;
        this.updateOriginalData();

        try
        {
            const thePagination = PaginationRequest
                                .withPageNumber(page)
                                .addSorting(this.sortField == 'id' ? 'displayId' : this.sortField, this.sortOrder)
                                .addFilter(PaginationFilter.create(this.isSearchEnabled && this.searchValue != '', this.searchField || '', this.searchValue, true))
                                .addFilter(PaginationFilter.create(true && this.statusFilter != '', 'status', this.statusFilter, false))
                                .addFilter(PaginationFilter.create(true && this.platformFilter != '', 'platform', this.platformFilter, false));
            if(this.pagination.pageSize) thePagination.pageSize = this.pagination.pageSize;
            let result = await this.loader(thePagination);
            this.onDataLoaded(result.data);
            this.pagination = result.pagination || new PaginationResponse();
            this.updateOriginalData();
        }
        catch (e)
        {
            this.isError = true;
            throw e;
        }
        finally
        {
            this.isLoading = false;
        }
    }
}